import React, { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { useQueryClient } from 'react-query'
import { useDispatch } from 'react-redux'

import { filtrosArrayBarra } from '../../../redux/actions/index'
import { PortadorasService } from '../../../services/http/portadoras.service'
import { useGlobal } from '../../../stores/useGlobal'
import {
  formatArrayToString,
  formatStringToNumberArray
} from '../../../utils/genericFormatters'
import Grid from '../../Mantine/Grid'
import Modal from '../../Mantine/Modal'
import MultiSelect from '../../Mantine/MultiSelect'
import Select from '../../Mantine/Select'

import * as S from './styles'

// eslint-disable-next-line no-magic-numbers
const WIDTH_COLUMNS = [4, 4, 4, 4, 4]

const DEFAULT_FILTERS_VALUES = {
  operadoras: [],
  tecnologias: [],
  estados: [],
  cidades: []
}

const FILTERS = [
  { label: 'Rede', value: 'operadoras', filterKey: 'operadoras' },
  {
    label: 'Classificação da Rede',
    value: 'classificacao_rede',
    filterKey: 'classificacao_rede'
  },
  { label: 'Tecnologia', value: 'tecnologias', filterKey: 'tecnologias' },
  { label: 'Estado', value: 'estados', filterKey: 'estados' },
  { label: 'Cidade', value: 'cidades', filterKey: 'cidades' }
]

const mapCarriers = (carriers) =>
  (carriers || []).map((item) => ({
    value: item.carrierID,
    label: item.carrierName
  }))

const mapTechnologies = (technologies) =>
  (technologies || []).map((item) => ({
    value: item.technologyID,
    label: item.technologyName
  }))

const mapStates = (address) =>
  (address || []).map((item) => ({
    value: item.stateID,
    label: item.stateName
  }))

const mapCarrierTypes = (carriers) =>
  (carriers || []).map((item) => ({
    value: item.cdTipo,
    label: item.portadoraTipo
  }))

export default function FiltroPortadoras({ opened, close }) {
  const { filtros, setFiltros, setRevelarBarraFiltro, setIsLoadingGlobal } =
    useGlobal()
  const despacho = useDispatch()

  const [filterOptions, setFilterOptions] = useState({
    operadoras: [],
    tecnologias: [],
    estados: [],
    cidades: [],
    classificacao_rede: []
  })
  const [localFilters, setLocalFilters] = useState({
    ...DEFAULT_FILTERS_VALUES
  })
  const [estado, setEstado] = useState(null)

  const {
    getCarrierFilter,
    getTechnologiesFilter,
    getAddressFilter,
    getCarriersTypesFilter
  } = PortadorasService

  let arrayPortadorasFilters = []

  const queryClient = useQueryClient()

  const dadosEmCache = queryClient.getQueryData(['portadoras/filter'])

  const fetchFilters = async () => {
    const [carriers, technologies, address, carrierTypes] = await Promise.all([
      getCarrierFilter(),
      getTechnologiesFilter(),
      getAddressFilter(),
      getCarriersTypesFilter()
    ])

    return {
      carriers: mapCarriers(carriers),
      technologies: mapTechnologies(technologies),
      states: mapStates(address),
      carrierTypes: mapCarrierTypes(carrierTypes)
    }
  }

  useEffect(() => {
    if (dadosEmCache) {
      setFilterOptions((prev) => ({
        ...prev,
        operadoras: dadosEmCache.carriers,
        tecnologias: dadosEmCache.technologies,
        estados: dadosEmCache.states,
        classificacao_rede: dadosEmCache.carrierTypes
      }))
    }
  }, [dadosEmCache])

  useQuery(
    ['portadoras/filter'],
    async () => {
      try {
        setIsLoadingGlobal(true)

        return await fetchFilters()
      } catch (err) {
        console.error(err)
      } finally {
        setIsLoadingGlobal(false)
      }
    },
    {
      onSuccess: (data) => {
        setFilterOptions((prev) => ({
          ...prev,
          operadoras: data.carriers,
          tecnologias: data.technologies,
          estados: data.states,
          classificacao_rede: data.carrierTypes
        }))

        if (filtros.CIDADE) refetch()
      },
      staleTime: 600000, //* 10 minutos
      cacheTime: 600000, //* Mantém no cache por 10 minutos após ficar inativo
      keepPreviousData: true,
      refetchOnWindowFocus: false
    }
  )

  const { refetch } = useQuery(
    ['get-city', estado],
    async () => {
      try {
        setIsLoadingGlobal(true)
        const address = await getAddressFilter(estado)

        setFilterOptions((prev) => {
          if (localFilters?.cidades) {
            const validCityIDs = new Set(address.map((city) => city.cityID))

            //* remove as cidades que foram selecionadas do estado removido
            setLocalFilters((prev) => ({
              ...prev,
              cidades: prev?.cidades?.filter((item) =>
                validCityIDs.has(item.value)
              )
            }))
          }

          return {
            ...prev,
            cidades: (address || [])?.map((item) => ({
              value: item.cityID,
              label: item.cityName,
              stateId: item.stateID
            }))
          }
        })
      } catch (err) {
        console.error(err)
      } finally {
        setIsLoadingGlobal(false)
      }
    },
    {
      enabled: !!estado,
      keepPreviousData: true,
      refetchOnWindowFocus: false
    }
  )

  useEffect(() => {
    if (!filtros.vazio) {
      const auxFilter = {
        ...DEFAULT_FILTERS_VALUES
      }

      if (filtros?.OPERADORA) {
        auxFilter.operadoras = formatStringToNumberArray(filtros.OPERADORA)
      }

      if (filtros?.TECNOLOGIA) {
        auxFilter.tecnologias = formatStringToNumberArray(filtros.TECNOLOGIA)
      }

      if (filtros?.ESTADO) {
        const estados = formatStringToNumberArray(filtros.ESTADO)
        auxFilter.estados = estados
        setEstado(estados)
      }

      if (filtros?.CIDADE && filtros?.CIDADE.length > 0) {
        auxFilter.cidades = [...filtros.CIDADE]
      }

      if (filtros?.TIPO) {
        auxFilter.classificacao_rede = Number(filtros.TIPO)
      }

      setLocalFilters(auxFilter)
    }
  }, [filtros])

  const handleLocalFiltroChange = (fieldPath, value) => {
    if (value && fieldPath === 'cidades') {
      const filteredOptions = filterOptions.cidades.filter((f) =>
        value.includes(f.value)
      )

      setLocalFilters((prev) => {
        return {
          ...prev,
          cidades: filteredOptions.map((item) => ({
            value: item.value,
            stateId: item.stateId
          }))
        }
      })

      return
    }

    if (value && fieldPath === 'estados') {
      setEstado(value)
    }

    const [parent, child] = fieldPath.split('.')
    setLocalFilters((prev) => {
      if (!child) {
        return {
          ...prev,
          [parent]: value
        }
      }

      return {
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }
    })
  }

  const formattedFilter = () => {
    const addFilters = (localFilterKey, filterOptionKey, prefix = '') => {
      const isCidades = localFilterKey === 'cidades'
      const isClassificacao = localFilterKey === 'classificacao_rede'

      const filteredItems = filterOptions[filterOptionKey].filter((item) => {
        if (isCidades) {
          return localFilters[localFilterKey].some(
            (filter) => filter.value === item.value
          )
        }

        if (isClassificacao) {
          return localFilters[localFilterKey] === item.value
        }

        return localFilters[localFilterKey].includes(item.value)
      })

      const formattedItems = filteredItems.map(
        (item) => `${prefix}${item.label?.trim()} _${item.value}`
      )

      arrayPortadorasFilters.push(...formattedItems)
    }

    if (localFilters?.operadoras?.length)
      addFilters('operadoras', 'operadoras', 'operadoraOperator: ')
    if (localFilters.tecnologias?.length)
      addFilters('tecnologias', 'tecnologias', 'tecnologiaOperator: ')
    if (localFilters.estados?.length)
      addFilters('estados', 'estados', 'estadoOperator: ')
    if (localFilters.cidades?.length)
      addFilters('cidades', 'cidades', 'cidadeOperator: ')
    if (localFilters.classificacao_rede)
      addFilters(
        'classificacao_rede',
        'classificacao_rede',
        'classificacaoRedeOperator: '
      )

    let label

    if (filtros.ID_PORTADORA) {
      let idsPortadoras = Array.isArray(filtros.ID_PORTADORA)
        ? filtros.ID_PORTADORA
        : filtros.ID_PORTADORA.split(',')

      const limite = 2
      const totalPortadoras = idsPortadoras.length

      if (totalPortadoras <= limite) {
        label = `idPortadoraOperator: ${idsPortadoras.join(', ')}`
      } else {
        const portadorasExibidas = idsPortadoras.slice(0, limite)
        const restante = totalPortadoras - limite
        label = `idPortadoraOperator: ${portadorasExibidas.join(', ')}... +${restante}`
      }

      arrayPortadorasFilters.push(label)
    }
    if (filtros.GEOLOCATION)
      arrayPortadorasFilters.push('Geolocalização?GEOLOCATION')

    despacho(filtrosArrayBarra(arrayPortadorasFilters))
  }

  const handleFilter = () => {
    const filteredData = {
      OPERADORA: formatArrayToString(localFilters.operadoras),
      CIDADE: localFilters.cidades,
      TECNOLOGIA: formatArrayToString(localFilters.tecnologias),
      ESTADO: formatArrayToString(localFilters.estados),
      TIPO: localFilters.classificacao_rede
        ? String(localFilters.classificacao_rede)
        : null
    }

    let finalFiltros = Object.fromEntries(
      Object.entries(filteredData).filter(
        ([, value]) => value && value.length > 0
      )
    )

    finalFiltros = {
      ...finalFiltros,
      ...(filtros.ID_PORTADORA && { ID_PORTADORA: filtros.ID_PORTADORA }),
      ...(filtros.GEOLOCATION && { GEOLOCATION: filtros.GEOLOCATION })
    }

    setFiltros(finalFiltros)
    setRevelarBarraFiltro(true)
    formattedFilter()
    close()
  }

  const handleCleanFilters = () => {
    setLocalFilters(DEFAULT_FILTERS_VALUES)
    setFiltros({ vazio: 'vazio' })
    despacho(filtrosArrayBarra([]))
    setRevelarBarraFiltro(false)
  }

  return (
    <Modal
      width="900px"
      type="filtro"
      titulo="Filtros"
      opened={opened}
      close={close}
      onConfirm={handleFilter}
      handleCleanFilters={handleCleanFilters}
    >
      <S.Container>
        <S.Card>
          <Grid responsive grow colSize={WIDTH_COLUMNS}>
            {FILTERS.map(({ label, value, filterKey }) => {
              const auxValue =
                filterKey === 'cidades'
                  ? localFilters[value].map((item) => item.value)
                  : localFilters[value]

              if (filterKey === 'classificacao_rede') {
                return (
                  <Select
                    placeholder="Selecione"
                    label={label}
                    key={value}
                    value={auxValue}
                    options={filterOptions?.[value]}
                    onChange={(selectedValue) =>
                      handleLocalFiltroChange(filterKey, selectedValue)
                    }
                  />
                )
              }

              return (
                <MultiSelect
                  searchable
                  disabled={value === 'cidades' && !localFilters.estados.length}
                  key={value}
                  label={label}
                  value={auxValue}
                  options={filterOptions?.[value]}
                  onChange={(selectedValue) =>
                    handleLocalFiltroChange(filterKey, selectedValue)
                  }
                />
              )
            })}
          </Grid>
        </S.Card>
      </S.Container>
    </Modal>
  )
}
